import QueryStats from "@mui/icons-material/QueryStats";
import { Alert, Box, Link, Skeleton } from "@mui/material";
import type { FC } from "react";
import { isEmailChannel } from "../../../modules/channels/types";
import { useAccount } from "../../../modules/generic/hooks/use-account";
import type { AnalyticsMoment } from "./types";
import { MomentAnalyticsProvider, useMomentAnalyticsContext } from "./context/moment-stats-context";
import { EmptyState } from "./generic/empty-state";
import { useGroupedMomentAnalyticsDataSource } from "./hooks/use-grouped-moment-analytics-data";
import { useMomentAnalyticsTotals } from "./hooks/use-moment-stats-totals";
import { useSendTableColumns } from "./hooks/use-send-table-columns";
import { AnalyticsOverview } from "./overview/overview";
import { AnalyticsSendsTable } from "./send-table/send-table";

const MomentAnalyticsContentState: FC<{
  moment: AnalyticsMoment;
  label?: string;
  detailsPath?: string;
}> = ({ moment, label = "Communication", detailsPath }) => {
  const { totals } = useMomentAnalyticsContext();
  const send_table_columns = useSendTableColumns();
  const statsDataSource = useGroupedMomentAnalyticsDataSource({ id: moment.id });
  const { loading: accountLoading, data: account } = useAccount();

  if (!totals) return null;

  const { loading, data } = totals;

  if (loading || !data || accountLoading || !account) {
    return (
      <Skeleton
        data-testid="analytics-content-skeleton"
        animation="wave"
        variant="rounded"
        height={250}
      />
    );
  }
  // Empty state means nothing has been sent yet (e.g. future scheduled)
  // The outbox could have items in progress, but none have been delivered (or failed to deliver)
  const isEmptyState = data.delivery_count === 0 && data.error_count === 0;

  if (isEmptyState) {
    return (
      <EmptyState
        symbol={<QueryStats />}
        title="Nothing to report yet"
        sx={(theme) => ({
          border: 2,
          borderRadius: 1,
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.active,
          textAlign: "center",
          p: 4,
        })}
        subtitle={
          <>
            <Box>
              You’ll be able to see stats on delivery, interactions, and issues here once your{" "}
              {label} has sent for the first time.
            </Box>
            <Box
              sx={{
                mt: 2,
              }}
            >
              You can see information on your {label}&nbsp;
              <Link href={detailsPath ?? `/moments/${moment.id}/details`} underline="hover">
                under the Details tab
              </Link>
            </Box>
          </>
        }
      />
    );
  }

  return (
    <>
      {!account.enable_tracking_pixel && isEmailChannel(moment.channel) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your company has opted not to include tracking images in email communications. As a
          result, open rate metrics will be inaccurate or unavailable.
        </Alert>
      )}
      <AnalyticsOverview moment={moment} />
      <AnalyticsSendsTable columns={send_table_columns} dataSource={statsDataSource} />
    </>
  );
};

export const MomentAnalyticsTab: FC<{
  moment: AnalyticsMoment;
  label?: string;
  detailsPath?: string;
}> = ({ moment, label, detailsPath }) => {
  const totals = useMomentAnalyticsTotals(moment);

  return (
    <MomentAnalyticsProvider totals={totals}>
      <MomentAnalyticsContentState moment={moment} label={label} detailsPath={detailsPath} />
    </MomentAnalyticsProvider>
  );
};
